// Customer Performance Settings API
import api, { type AxiosResponse } from './client';
import type {
  CustomerPerformanceSettings,
  CustomerPerformanceSettingsUpdate,
  SiteKPISettings,
  CustomerRatingThreshold,
  CreateRatingThresholdData,
  OTIFScoreMapping,
  PPMScoreMapping,
  QueryParams,
} from './types';

export const performanceSettingsAPI = {
  // Get customer performance settings for current company
  getSettings: (): Promise<AxiosResponse<CustomerPerformanceSettings>> =>
    api.get('/core/settings/performance/'),

  // Update customer performance settings
  updateSettings: (data: CustomerPerformanceSettingsUpdate): Promise<AxiosResponse<{ message: string; data: CustomerPerformanceSettings }>> =>
    api.patch('/core/settings/performance/', data),

  // Site KPI Settings
  getSiteKPISettings: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: SiteKPISettings[] }>> =>
    api.get('/core/site-kpi-settings/', { params }),

  getSiteKPISettingsForSite: (siteId: number): Promise<AxiosResponse<SiteKPISettings>> =>
    api.get('/core/site-kpi-settings/for_site/', { params: { site_id: siteId } }),

  updateSiteKPISettings: (id: number, data: Partial<SiteKPISettings>): Promise<AxiosResponse<SiteKPISettings>> =>
    api.patch(`/core/site-kpi-settings/${id}/`, data),

  // Rating Thresholds
  getRatingThresholds: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: CustomerRatingThreshold[] }>> =>
    api.get('/core/rating-thresholds/', { params }),

  createRatingThreshold: (data: CreateRatingThresholdData): Promise<AxiosResponse<CustomerRatingThreshold>> =>
    api.post('/core/rating-thresholds/', data),

  updateRatingThreshold: (id: number, data: Partial<CustomerRatingThreshold>): Promise<AxiosResponse<CustomerRatingThreshold>> =>
    api.patch(`/core/rating-thresholds/${id}/`, data),

  deleteRatingThreshold: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/core/rating-thresholds/${id}/`),

  resetRatingThresholds: (performanceSettingsId: number): Promise<AxiosResponse<{ message: string; thresholds: CustomerRatingThreshold[] }>> =>
    api.post('/core/rating-thresholds/reset_defaults/', { performance_settings_id: performanceSettingsId }),

  // OTIF Score Mappings
  getOTIFScoreMappings: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: OTIFScoreMapping[] }>> =>
    api.get('/core/otif-score-mappings/', { params }),

  updateOTIFScoreMapping: (id: number, data: Partial<OTIFScoreMapping>): Promise<AxiosResponse<OTIFScoreMapping>> =>
    api.patch(`/core/otif-score-mappings/${id}/`, data),

  resetOTIFScoreMappings: (performanceSettingsId: number): Promise<AxiosResponse<{ message: string; mappings: OTIFScoreMapping[] }>> =>
    api.post('/core/otif-score-mappings/reset_defaults/', { performance_settings_id: performanceSettingsId }),

  // PPM Score Mappings
  getPPMScoreMappings: (params?: QueryParams): Promise<AxiosResponse<{ count: number; results: PPMScoreMapping[] }>> =>
    api.get('/core/ppm-score-mappings/', { params }),

  updatePPMScoreMapping: (id: number, data: Partial<PPMScoreMapping>): Promise<AxiosResponse<PPMScoreMapping>> =>
    api.patch(`/core/ppm-score-mappings/${id}/`, data),

  resetPPMScoreMappings: (performanceSettingsId: number): Promise<AxiosResponse<{ message: string; mappings: PPMScoreMapping[] }>> =>
    api.post('/core/ppm-score-mappings/reset_defaults/', { performance_settings_id: performanceSettingsId }),

  // Customer OTIF Override (CR-3375)
  updateCustomerOTIFOverride: (
    customerProfileId: number,
    data: {
      use_custom_otif_window?: boolean;
      otif_days_early_allowed?: number;
      otif_days_late_allowed?: number;
    }
  ): Promise<AxiosResponse<{
    message: string;
    data: {
      id: number;
      company_id: number;
      company_name: string;
      use_custom_otif_window: boolean;
      otif_days_early_allowed: number;
      otif_days_late_allowed: number;
    };
  }>> => api.patch(`/core/customer-otif-override/${customerProfileId}/`, data),
};
